import React, { useCallback, useMemo } from 'react';
import { CustomFilterProps, useGridFilter } from 'ag-grid-react';
import { IDoesFilterPassParams } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import './ChecklistFilter.scss';

export interface ChecklistFilterOption {
  searchKey: string;
  translationKey: string;
}

export interface ChecklistFilterModel {
  filterType: 'checklist';
  values: string[];
}

export interface ChecklistFilterParams
  extends CustomFilterProps<any, any, ChecklistFilterModel> {
  options: ChecklistFilterOption[];
  initialList?: string[];
  updateFilter?: (updatedList: string[]) => void;
}

export default function ChecklistFilter({
  model,
  onModelChange,
  getValue,
  options,
  initialList,
  updateFilter,
}: ChecklistFilterParams) {
  const { t } = useTranslation();

  const defaultKeys = useMemo(
    () => options.map((opt) => opt.searchKey),
    [options]
  );
  const initialKeys = initialList ?? defaultKeys;

  const selectedKeys = model?.values ?? initialKeys;
  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  const isSelected = useCallback(
    (searchKey: string) => selectedSet.has(searchKey),
    [selectedSet]
  );

  const allItemsSelected = useMemo(
    () =>
      options.length > 0 &&
      options.every((opt) => selectedSet.has(opt.searchKey)),
    [options, selectedSet]
  );

  const applySelection = useCallback(
    (keys: string[]) => {
      const isAllSelected = keys.length === options.length;

      onModelChange(
        isAllSelected ? null : { filterType: 'checklist', values: keys }
      );

      updateFilter?.(isAllSelected ? defaultKeys : keys);
    },
    [defaultKeys, onModelChange, options.length, updateFilter]
  );

  const onItemClick = useCallback(
    (item: ChecklistFilterOption) => {
      const nextKeys = isSelected(item.searchKey)
        ? selectedKeys.filter((key) => key !== item.searchKey)
        : [...selectedKeys, item.searchKey];

      applySelection(nextKeys);
    },
    [applySelection, isSelected, selectedKeys]
  );

  const onSelectAll = useCallback(() => {
    if (allItemsSelected) {
      applySelection([]);
    } else {
      applySelection(defaultKeys);
    }
  }, [allItemsSelected, applySelection, defaultKeys]);

  const doesFilterPass = useCallback(
    (filterParams: IDoesFilterPassParams) => {
      if (selectedKeys.length === options.length) {
        return true;
      }

      const value = getValue(filterParams.node);
      if (value == null) {
        return false;
      }

      return selectedSet.has(String(value));
    },
    [getValue, options.length, selectedKeys.length, selectedSet]
  );

  useGridFilter({
    doesFilterPass,
  });

  return (
    <div className="ag-grid-custom-filter">
      <div className="filter-menu-arrow" />
      <div className="filter-menu">
        <ul className="filter-menu-div">
          <ChecklistItem
            isSelected={allItemsSelected}
            label={t('common.grid.selectAll')}
            onClick={onSelectAll}
          />

          <hr className="dropdown-divider" />

          {options.map((item) => (
            <ChecklistItem
              key={item.searchKey}
              isSelected={isSelected(item.searchKey)}
              label={t(item.translationKey)}
              onClick={() => onItemClick(item)}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

interface ChecklistItemProps {
  isSelected: boolean;
  label: string;
  onClick: () => void;
}

function ChecklistItem({ isSelected, label, onClick }: ChecklistItemProps) {
  return (
    <li>
      <label>
        <input
          type="checkbox"
          className="d-none"
          checked={isSelected}
          onChange={onClick}
        />
        <div className="checkbox-icon">
          {isSelected ? (
            <div className="checked">
              <i className="icon fas fa-check" />
            </div>
          ) : (
            <div className="unchecked" />
          )}
        </div>
        <div className="checkbox-label">
          <span>{label}</span>
        </div>
      </label>
    </li>
  );
}
